import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import moment from 'moment';
import {
  IconCellRenderer,
  LinkCellRenderer,
  confirm,
  DropdownInput,
} from '@trueprodigy/components';
import CustomUploadInput from '@components/CustomUploadInput';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import { Field, reduxForm } from 'redux-form/immutable';
import styled from 'styled-components';
import { Card } from '@modules/appraisal/pages/propertyPage/components/cardStyledComponents';
import imageNotFound from '@assets/images/image-not-found.png';
import Tooltip from '@material-ui/core/Tooltip';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import AgGrid from '@components/AgGrid/AgGrid';
import { fetchingState, RIGHTS } from '@constants';
import * as eventActions from '@modules/events/actions/event';
import {
  eventDataSelector,
  eventDocumentListSelector,
  eventDocumentListStateSelector,
} from '@modules/events/selectors/event';
import { userDataSelector, userStatusSelector } from '@selectors/global';
import { hasRight } from '@utils';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import SvgIcon from '@material-ui/core/SvgIcon';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import CloseIcon from '@material-ui/icons/Close';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { globalGetByteArray } from '@api/request';
import * as propertyActions from '@modules/appraisal/actions/property';
import * as propertySelectors from '@modules/appraisal/selectors/property';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

const extToMimes = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  pdf: 'application/pdf',
  tif: 'image/tiff',
  tiff: 'image/tiff',
};

const buildColumnDefs = ({ openDocument, handleDelete, disabled }) => [
  {
    headerName: 'Actions',
    field: 'fileAttachmentActions',
    width: 120,
    maxWidth: 120,
    minWidth: 120,
    cellRenderer: IconCellRenderer,
    cellRendererParams: {
      icons: [
        {
          icon: !disabled && 'fa-trash',
          onClick: (props, record) => handleDelete({ s3ID: record.s3ID }),
        },
      ],
    },
  },
  { headerName: 'Description', field: 'description', editable: true },
  { headerName: 'Date Added', field: 'dateAdded' },
  { headerName: 'Added By', field: 'addedBy' },
  {
    headerName: 'File',
    field: 's3ID',
    cellRenderer: LinkCellRenderer,
    cellRendererParams: {
      onClick: (s3ID) => openDocument({ s3ID }),
    },
  },
];

const FileAttachmentsForm = ({
  openDocument,
  documentList,
  documentListState,
  handleDelete,
  handleSubmit,
  onCellValueChanged,
  selectedDescription,
  setSelectedDescription,
  hasEventRight,
  userStatus,
}) => {
  const dispatch = useDispatch();
  const [mode, setMode] = useState('list');
  const [dialogImage, setDialogImage] = useState(false);
  const [descriptionOptions, setDescriptionOptions] = useState([]);
  const [documentDataList, setDocumentDataList] = useState(
    JSON.parse(JSON.stringify(documentList)),
  );
  const [loaded, setLoaded] = useState(false);

  const property = useSelector(propertySelectors.propertySelector).toJS().data;
  const primaryImageUrl = useSelector(
    propertySelectors.propertyPrimaryImageSelector,
  );
  const isReadOnly = userStatus.get('readOnly');

  useEffect(() => {
    if (!loaded) {
      fetchDescriptionOptions();
      if (property) {
        dispatch(
          propertyActions.fetchPropertyPrimaryImageUrl({
            pId: property.pid,
            pYear: property.pYear,
            pVersion: property.pVersion,
            pRollCorr: property.pRollCorr,
          }),
        );
      }
      setLoaded(true);
    }
  }, [property]);

  useEffect(() => {
    if (mode === 'thumbnail') {
      convertData();
    }
  }, [mode]);

  const convertData = async () => {
    let temp = JSON.parse(JSON.stringify(documentList));
    for (let x = 0; x < temp.length; x++) {
      let localUrl = null;
      let imageType = 'image';
      let image = temp[x];
      const ext = getExtension(image.signedURL);
      if (['pdf', 'tiff', 'tif'].includes(ext)) {
        const type = extToMimes[ext];
        if (image.size === 0) {
          localUrl = imageNotFound;
        } else if (!image.size) {
          // } else if (image.size <= MAX_DOC_SIZE) {
          imageType = 'document';
          try {
            if (type === 'image/tiff') {
              const response = await globalGetByteArray(image.signedURL);
              window.Tiff.initialize({ TOTAL_MEMORY: 100000000 });
              const tiff = new window.Tiff({ buffer: response });
              const canvas = tiff.toCanvas();
              localUrl = canvas.toDataURL();
            } else {
              const doc = await pdfjsLib.getDocument(image.signedURL).promise;
              const canvas = document.createElement('canvas');
              const firstPage = await doc.getPage(1);
              await makeThumbnail(firstPage, canvas);
              localUrl = canvas.toDataURL();
            }
          } catch (err) {
            console.log(err, err.message);
            localUrl = imageNotFound;
          }
        } else {
          localUrl = temp[x].signedURL;
        }
      } else {
        localUrl = temp[x].signedURL;
      }
      // temp[x].viewURL = temp[x].signedURL;
      temp[x].viewURL = localUrl;
      temp[x].imageType = imageType;
      temp[x].signedURL = image.signedURL;
      temp[x].s3ID = image.s3ID;
    }
    setDocumentDataList(temp);
  };

  const makeThumbnail = async (page, canvas) => {
    canvas.width = canvas.height = 800;
    const vp = page.getViewport({ scale: 1 });
    var scale = Math.min(canvas.width / vp.width, canvas.height / vp.height);
    var viewport = page.getViewport({ scale: scale });
    const canvasContext = canvas.getContext('2d');
    const renderContext = {
      canvasContext,
      viewport,
    };
    await page.render(renderContext).promise;
  };

  const setPrimaryImage = (imageUrl, key) => {
    if (
      !(
        primaryImageUrl.toJS().data &&
        primaryImageUrl.toJS().data.primaryImagePath &&
        imageUrl &&
        imageUrl.includes(
          primaryImageUrl
            .toJS()
            .data.primaryImagePath.replace(/^.+\//gm, '')
            .replace(/\..+$/gm, ''),
        )
      )
    ) {
      let data = {
        primaryImagePath: key,
        lastUpdate: primaryImageUrl.toJS().data.updateDt,
      };
      dispatch(
        propertyActions.updatePropertyPrimaryImage({
          propertyId: property.pid,
          pYear: property.pYear,
          pVersion: property.pVersion,
          pRollCorr: property.pRollCorr,
          data: data,
        }),
      );
    } else {
      let data = {
        primaryImagePath: null,
        lastUpdate: primaryImageUrl.toJS().data.updateDt,
      };
      dispatch(
        propertyActions.updatePropertyPrimaryImage({
          propertyId: property.pid,
          pYear: property.pYear,
          pVersion: property.pVersion,
          pRollCorr: property.pRollCorr,
          data: data,
        }),
      );
    }
  };

  const getExtension = (url) => {
    if (!url) return;
    const fileUrl = url.slice(0, url.indexOf('?'));
    const temp = fileUrl.slice(fileUrl.lastIndexOf('.') + 1);
    if (temp) {
      return temp.toLowerCase();
    }
    return temp;
  };

  const fetchDescriptionOptions = async () => {
    let data = await dispatch(eventActions.fetchDescriptionCodefile());
    if (data && data.results && data.results.length > 0) {
      setDescriptionOptions(
        data.results.map((r) => {
          return {
            label: r.description,
            value: r.description,
          };
        }),
      );
    }
  };

  const rotate64 = (base64data, degrees, url) => {
    return new Promise((resolve, reject) => {
      degrees = degrees ? degrees : 90;
      var canvas = document.createElement('canvas');
      canvas.setAttribute('id', 'hidden-canvas');
      canvas.style.display = 'none';
      document.body.appendChild(canvas);

      var ctx = canvas.getContext('2d');

      const image = new Image();
      image.src = base64data;
      image.onload = () => {
        var w = image.width;
        var h = image.height;
        var rads = (degrees * Math.PI) / 180;
        var c = Math.cos(rads);
        var s = Math.sin(rads);
        if (s < 0) {
          s = -s;
        }
        if (c < 0) {
          c = -c;
        }
        canvas.width = h * s + w * c;
        canvas.height = h * c + w * s;
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.drawImage(image, -image.width / 2, -image.height / 2);
        const newUrl = canvas.toDataURL();
        let temp = JSON.parse(JSON.stringify(documentDataList));
        for (let x = 0; x < temp.length; x++) {
          if (temp[x].signedURL === url) {
            temp[x].viewURL = newUrl;
          }
        }
        setDocumentDataList(temp);
        document.body.removeChild(canvas);
        resolve(newUrl);
      };
      image.onerror = () => {
        console.log('Unable to rotate data\n' + image.src);
        reject('Unable to rotate data\n' + image.src);
      };
    });
  };

  return (
    <FileAttachmentsForm.Container>
      <FileAttachmentsForm.ButtonContainer>
        <Typography
          style={{ fontSize: '30px', fontWeight: '600' }}
          variant="h4"
        >
          {mode === 'list' ? 'FILE ATTACHMENTS' : 'ATTACHMENT GALLERY'}
        </Typography>
        <ToggleButtonGroup
          value={[mode]}
          onChange={(e, i) => {
            if (i.length > 0 && i[1] === 'thumbnail') {
              setMode('thumbnail');
            }
            if (i.length > 0 && i[1] === 'list') {
              setMode('list');
            }
          }}
          aria-label="text formatting"
        >
          <ToggleButton value="list" aria-label="list">
            <SvgIcon viewBox={'0 0 15 11'}>
              <path
                fill={mode === 'list' ? '#2079e2' : '#747474'}
                d="M2.2 56.782H.439a.439.439 0 0 0-.439.439v1.756a.439.439 0 0 0 .439.439H2.2a.439.439 0 0 0 .439-.439v-1.756a.439.439 0 0 0-.439-.439zM2.2 48H.439a.439.439 0 0 0-.439.439V50.2a.439.439 0 0 0 .439.439H2.2a.439.439 0 0 0 .439-.439v-1.761A.439.439 0 0 0 2.2 48zm0 4.391H.439A.439.439 0 0 0 0 52.83v1.756a.439.439 0 0 0 .439.439H2.2a.439.439 0 0 0 .439-.439V52.83a.439.439 0 0 0-.439-.439zm11.417 4.83H4.83a.439.439 0 0 0-.439.439v.878a.439.439 0 0 0 .439.439h8.782a.439.439 0 0 0 .439-.439v-.878a.439.439 0 0 0-.438-.439zm0-8.782H4.83a.439.439 0 0 0-.439.439v.878a.439.439 0 0 0 .439.439h8.782a.439.439 0 0 0 .439-.439v-.878a.439.439 0 0 0-.438-.439zm0 4.391H4.83a.439.439 0 0 0-.439.439v.878a.439.439 0 0 0 .439.439h8.782a.439.439 0 0 0 .439-.439v-.878a.439.439 0 0 0-.438-.439z"
                transform="translate(0 -48)"
              />
            </SvgIcon>
          </ToggleButton>
          <ToggleButton value="thumbnail" aria-label="thumbnail">
            <SvgIcon viewBox={'0 0 15 11'}>
              <path
                fill={mode === 'thumbnail' ? '#2079e2' : '#747474'}
                d="M7.525 32h4.881a.61.61 0 0 1 .61.61v4.068a.61.61 0 0 1-.61.61H7.525a.61.61 0 0 1-.61-.61V32.61a.61.61 0 0 1 .61-.61zm-2.034 0H.61a.61.61 0 0 0-.61.61v4.068a.61.61 0 0 0 .61.61h4.881a.61.61 0 0 0 .61-.61V32.61a.61.61 0 0 0-.61-.61zM0 38.712v4.068a.61.61 0 0 0 .61.61h4.881a.61.61 0 0 0 .61-.61v-4.068a.61.61 0 0 0-.61-.61H.61a.61.61 0 0 0-.61.61zm7.525 4.678h4.881a.61.61 0 0 0 .61-.61v-4.068a.61.61 0 0 0-.61-.61H7.525a.61.61 0 0 0-.61.61v4.068a.61.61 0 0 0 .61.609z"
                transform="translate(0 -32)"
              />
            </SvgIcon>
          </ToggleButton>
        </ToggleButtonGroup>
        {/* <FileAttachmentsForm.Button
          enabled={mode === 'list'}
          onClick={() => {
            setMode('list');
          }}
        >
          List View
        </FileAttachmentsForm.Button>
        <FileAttachmentsForm.Button
          enabled={mode === 'thumbnail'}
          onClick={() => {
            setMode('thumbnail');
          }}
        >
          Thumbnail View
        </FileAttachmentsForm.Button> */}
      </FileAttachmentsForm.ButtonContainer>
      {mode === 'list' ? (
        <FileAttachmentsForm.Wrapper onSubmit={handleSubmit}>
          <FileAttachmentsForm.UploadSection>
            <Field
              component={(props) => (
                <CustomUploadInput
                  multiple
                  disabled={isReadOnly || !hasEventRight()}
                  {...props}
                />
              )}
              allowScan
              name="attachments"
            />
            <FileAttachmentsForm.DropdownWrapper>
              <DropdownInput
                disabled={isReadOnly || !hasEventRight()}
                options={descriptionOptions}
                addEmptyOption={true}
                placeholder={'Description'}
                label={'Description'}
                input={{
                  onChange: (selection) =>
                    setSelectedDescription(selection.value),
                  value: selectedDescription,
                }}
                meta={{
                  touched: () => {},
                }}
              />
            </FileAttachmentsForm.DropdownWrapper>
          </FileAttachmentsForm.UploadSection>

          <FileAttachmentsForm.TableWrapper className="ag-theme-material">
            {documentListState === fetchingState.FETCHING ? (
              <div style={{ textAlign: 'center' }}>
                <CircularProgress />
              </div>
            ) : (
              <AgGrid
                autoResizeToFit
                columnDefs={buildColumnDefs({
                  openDocument,
                  handleDelete,
                  disabled: isReadOnly || !hasEventRight(),
                })}
                disablePreferenceSave
                domLayout="autoHeight"
                gridOptions={{
                  onCellValueChanged: onCellValueChanged,
                }}
                name="fileAttachmentsGrid"
                rowData={documentList}
              />
            )}
          </FileAttachmentsForm.TableWrapper>
        </FileAttachmentsForm.Wrapper>
      ) : (
        <FileAttachmentsForm.PhotoWrapper>
          {documentDataList.map((r, i) => {
            return (
              <Card
                key={i}
                bgColor="#fff"
                style={{
                  position: 'relative',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  minWidth: 250,
                  maxWidth: 360,
                  maxHeight: 280,
                  overflow: 'hidden',
                  margin: 10,
                  justifyContent: 'flex-end',
                }}
              >
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        height: '27px',
                        backgroundColor: '#fff',
                        color: '#000',
                        display: 'flex',
                        borderRadius: '5px 5px 0 0',
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          padding: '5px 0 0 10px',
                          fontSize: '12px',
                        }}
                      >
                        {`${moment(r.dateAdded).format('MM/DD/YY')} | ${
                          r.addedBy
                        }`}
                      </div>
                      <div style={{ flexGrow: 1 }}></div>
                      {r.imageType === 'document' ? (
                        <div style={{ alignSelf: 'center', paddingRight: 5 }}>
                          <IconButton
                            style={{ padding: 2 }}
                            onClick={async () => {
                              await rotate64(r.viewURL, -90, r.signedURL);
                            }}
                          >
                            <RotateRightIcon />
                          </IconButton>
                        </div>
                      ) : null}
                      {r.imageType === 'image' && property ? (
                        <div style={{ alignSelf: 'center', paddingRight: 5 }}>
                          <Tooltip
                            title={
                              primaryImageUrl.toJS().data &&
                              primaryImageUrl.toJS().data.primaryImagePath &&
                              r.signedURL &&
                              r.signedURL.includes(
                                primaryImageUrl
                                  .toJS()
                                  .data.primaryImagePath.replace(/^.+\//gm, '')
                                  .replace(/\..+$/gm, ''),
                              )
                                ? 'Primary Photo'
                                : 'Set Primary Photo'
                            }
                            placement="bottom"
                          >
                            <IconButton
                              style={{ padding: 0 }}
                              onClick={() => {
                                setPrimaryImage(r.signedURL, r.s3ID);
                              }}
                            >
                              {primaryImageUrl.toJS().data &&
                                primaryImageUrl.toJS().data.primaryImagePath &&
                                r.signedURL &&
                                r.signedURL.includes(
                                  primaryImageUrl
                                    .toJS()
                                    .data.primaryImagePath.replace(
                                      /^.+\//gm,
                                      '',
                                    )
                                    .replace(/\..+$/gm, ''),
                                ) && (
                                  <StarIcon
                                    style={{
                                      color: '#303e9f',
                                      padding: 0,
                                    }}
                                  />
                                )}
                              {!(
                                primaryImageUrl.toJS().data &&
                                primaryImageUrl.toJS().data.primaryImagePath &&
                                r.signedURL &&
                                r.signedURL.includes(
                                  primaryImageUrl
                                    .toJS()
                                    .data.primaryImagePath.replace(
                                      /^.+\//gm,
                                      '',
                                    )
                                    .replace(/\..+$/gm, ''),
                                )
                              ) && (
                                <StarBorderIcon
                                  style={{
                                    color: '#303e9f',
                                    padding: 0,
                                  }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        </div>
                      ) : null}
                    </div>
                    <div style={{ height: '226px', width: '100%' }}>
                      <FileAttachmentsForm.StyledPhoto
                        src={r.viewURL}
                        style={{
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          // openDocument({ s3ID: r.s3ID });
                          setDialogImage({
                            viewUrl: r.viewURL,
                            signedURL: r.signedURL,
                            imageType: r.imageType,
                          });
                        }}
                        onError={() => {
                          let temp = JSON.parse(
                            JSON.stringify(documentDataList),
                          );
                          temp[i].viewURL = imageNotFound;
                          setDocumentDataList(temp);
                        }}
                      />
                    </div>
                    <div
                      style={{
                        height: '27px',
                        backgroundColor: '#fff',
                        color: '#000',
                        display: 'flex',
                        borderRadius: '0 0 5px 5px',
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          padding: '5px 0 0 10px',
                          fontSize: '12px',
                          overflow: 'hidden',
                        }}
                      >
                        {`${r.description}`}
                      </div>
                      <div style={{ flexGrow: 1 }}></div>
                      <div style={{ alignSelf: 'center', paddingRight: 5 }}>
                        <IconButton
                          style={{ padding: 2 }}
                          onClick={() => {
                            openDocument({ s3ID: r.s3ID });
                          }}
                        >
                          <CloudDownloadIcon />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                </>
              </Card>
            );
          })}
        </FileAttachmentsForm.PhotoWrapper>
      )}
      <Dialog
        onClose={() => setDialogImage(null)}
        open={!!dialogImage}
        maxWidth="xl"
      >
        <DialogTitle
          id="simple-dialog-title"
          style={{ position: 'sticky', top: 0, background: '#fff' }}
        >
          Event Image
          {dialogImage && dialogImage.imageType === 'document' ? (
            <>
              <IconButton
                onClick={async () => {
                  const newUrl = await rotate64(
                    dialogImage.viewUrl,
                    -90,
                    dialogImage.signedURL,
                  );
                  setDialogImage({
                    ...dialogImage,
                    viewUrl: newUrl,
                  });
                }}
                style={{ marginLeft: 50 }}
              >
                <RotateLeftIcon />
              </IconButton>
              <IconButton
                onClick={async () => {
                  const newUrl = await rotate64(
                    dialogImage.viewUrl,
                    90,
                    dialogImage.signedURL,
                  );
                  setDialogImage({
                    ...dialogImage,
                    viewUrl: newUrl,
                  });
                }}
                style={{ marginLeft: 50 }}
              >
                <RotateRightIcon />
              </IconButton>
            </>
          ) : null}
          <FileAttachmentsForm.CloseButton onClick={() => setDialogImage(null)}>
            <CloseIcon />
          </FileAttachmentsForm.CloseButton>
        </DialogTitle>
        <img
          src={dialogImage && dialogImage.viewUrl}
          style={{
            minWidth: '80vw',
            objectFit: 'contain',
            objectPosition: 'top',
          }}
        />
      </Dialog>
    </FileAttachmentsForm.Container>
  );
};

FileAttachmentsForm.propTypes = {
  currentEvent: PropTypes.object,
  documentList: PropTypes.array,
  documentListState: PropTypes.string,
  eventID: PropTypes.string,
  handleDelete: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  hasEventRight: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  openDocument: PropTypes.func.isRequired,
  onCellValueChanged: PropTypes.func.isRequired,
  selectedDescription: PropTypes.any,
  setSelectedDescription: PropTypes.func.isRequired,
  userStatus: PropTypes.any,
};

FileAttachmentsForm.defaultProps = {
  currentEvent: {},
  documentList: [],
  documentListState: null,
};

FileAttachmentsForm.Container = styled.form`
  background: white;
  border-top: 4px solid #acb8cc;
  padding: 20px 35px 35px 35px;
`;

FileAttachmentsForm.Wrapper = styled.form`
  background: white;
  display: flex;
  flex-direction: row;
  min-height: 350px;
`;

FileAttachmentsForm.PhotoWrapper = styled.form`
  background: white;
  min-height: 350px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

FileAttachmentsForm.UploadSection = styled.div`
  width: 300px;
`;

FileAttachmentsForm.Header = styled.header`
  margin-bottom: 20px;
`;

FileAttachmentsForm.ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 0 15px 0;
`;

FileAttachmentsForm.Button = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 5px 10px 5px 10px;
  cursor: pointer;
  margin: 0 15px 0 0;
  border: 1px solid black;
  user-select: none;
  background: ${({ enabled }) => (enabled ? '#b7cd2a' : '#E3E3E3')};
`;

FileAttachmentsForm.CloseButton = styled(IconButton)`
  &&& {
    position: absolute;
    right: 10px;
    top: 10px;
    color: #888;
  }
`;

FileAttachmentsForm.TableWrapper = styled.div`
  width: calc(100% - 330px);
  margin-left: 30px;

  .ag-menu {
    max-height: 170px;
  }

  .ag-overlay-no-rows-center {
    display: none;
  }
`;

FileAttachmentsForm.StyledPhoto = styled.img`
  width: 100%;
  max-width: 100%;
  object-fit: cover;
`;

FileAttachmentsForm.DropdownWrapper = styled.div`
  padding-top: 10px;

  > div {
    width: 300px;
  }

  > div > div > div:first-child {
    width: 300px;
  }
`;

const mapStateToProps = (state) => ({
  currentEvent: eventDataSelector(state),
  documentList: eventDocumentListSelector(state),
  documentListState: eventDocumentListStateSelector(state),
  userData: userDataSelector(state),
  userStatus: userStatusSelector(state),
});

const mapDispatchToProps = {
  deleteDocument: eventActions.deleteDocument,
  getDocument: eventActions.getDocument,
  fetchDocuments: eventActions.fetchDocuments,
  createDocument: eventActions.createDocument,
  updateDocument: eventActions.updateDocument,
  setEvent: eventActions.setCurrentEvent,
};

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withState('selectedDescription', 'setSelectedDescription', null),
  withHandlers({
    hasEventRight:
      ({ userData }) =>
      () => {
        return hasRight(userData.get('userrights').toJS(), RIGHTS.EVENTS.EVENT);
      },
    handleSubmit:
      ({ createDocument, currentEvent, setEvent, selectedDescription }) =>
      async ({ attachments }) => {
        const { eventID, updateDt } = currentEvent;
        let actualUpdateDt = updateDt;

        for (const attachment of attachments) {
          const formData = new FormData();
          formData.append('file', attachment);
          formData.append('lastUpdate', actualUpdateDt);
          if (selectedDescription) {
            formData.append('description', selectedDescription);
          }
          const { results } = await createDocument({
            eventID,
            payload: formData,
          });
          actualUpdateDt = results[0].updateDt;
          await setEvent({ ...currentEvent, updateDt: results[0].updateDt });
        }
      },
    handleDelete:
      ({ deleteDocument, currentEvent, setEvent }) =>
      ({ s3ID }) => {
        const { eventID, updateDt } = currentEvent;

        return confirm('file')
          .then(() =>
            deleteDocument({
              payload: { lastUpdate: updateDt || '' },
              eventID,
              s3ID,
            }),
          )
          .then(({ results }) => {
            setEvent({ ...currentEvent, updateDt: results[0].updateDt });
          });
      },
    openDocument:
      ({ getDocument, currentEvent }) =>
      ({ s3ID }) => {
        getDocument({ eventID: currentEvent.eventID, s3ID });
      },
    onCellValueChanged:
      ({ updateDocument, currentEvent, setEvent }) =>
      (params) => {
        const { eventID, updateDt } = currentEvent;

        return updateDocument({
          payload: { ...params.data, lastUpdate: updateDt || '' },
          eventID,
        }).then(({ results }) => {
          setEvent({ ...currentEvent, updateDt: results[0].updateDt });
        });
      },
  }),
  lifecycle({
    componentDidMount() {
      this.props.fetchDocuments({ eventID: this.props.eventID });
    },
  }),
  reduxForm({
    form: 'eventFileAttachments',
    onChange: (values, dispatch, props) => {
      props.handleSubmit(values.toJS());
    },
  }),
);

export default enhance(FileAttachmentsForm);
